<template>
  <img v-bind="attrsImg" />
</template>

<script>
import {
  RAS_MEDIA_SERVER_HOSTNAME,
  BODY_TARGET_IDS,
} from "@/util/constants.ts";
import { doesMediaUrlExistInCache } from "@/sw-utils.ts";

const convertExt = new Map();
convertExt.set("tiff", "jpg");
convertExt.set("svg", "webp");
convertExt.set("jpeg", "jpg");
convertExt.set("pdf", "png");

export default {
  inheritAttrs: false,
  expose: ["dimensions"],
  props: {
    loadImageDimensions: {
      type: Boolean,
      default: false,
    },
    forceOriginalSize: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["loadComplete"],
  data() {
    return {
      progressive: false,
      mainFailed: false,
      cachedUrl: null,
      dimensions: {
        height: undefined,
        width: undefined,
        aspectRatio: undefined,
        isLandscape: undefined,
        isPortrait: undefined,
        isSquare: undefined,
      },
    };
  },
  computed: {
    attrsImg() {
      if (this.cachedUrl) {
        return {
          ...this.$attrs,
          src: this.cachedUrl,
          sizes: undefined,
        };
      }

      let attrs;
      if (this.isExternalMedia) {
        attrs = { ...this.$attrs };
      } else {
        attrs = { ...(this.progressive ? this.attrsProg : this.attrsMain) };
      }
      return attrs;
    },
    attrsMain() {
      const attrsIgnore = ["srcset"];
      const attrs = {};
      Object.keys(this.$attrs).forEach((key) => {
        if (attrsIgnore.indexOf(key) === -1) {
          attrs[key] = this.$attrs[key];
        }
      });
      if (!this.mainFailed) {
        const url = this.$attrs.src;
        const sizes = [640, 768, 1024, 1280, 1536, 1920];
        const srcset = [];
        sizes.forEach((width) => {
          srcset.push(this.getSrcUrl(url, width).concat(` ${width}w`));
        });
        attrs.srcset = srcset.join(", ");
      } else {
        delete attrs.sizes;
        delete attrs.srcset;
      }
      if (!attrs.class) {
        attrs.class = "";
      }
      attrs.class += " responsive-img main-img";
      return attrs;
    },
    attrsProg() {
      const attrsIgnore = ["srcset", "src", "sizes"];
      const attrs = {};
      Object.keys(this.$attrs).forEach((key) => {
        if (attrsIgnore.indexOf(key) === -1) {
          attrs[key] = this.$attrs[key];
        }
      });
      const url = this.$attrs.src;
      attrs.src = this.getSrcUrl(url, 48);
      if (!attrs.class) {
        attrs.class = "";
      }
      attrs.class += " responsive-img progressive-img";
      return attrs;
    },
    /*
     Internal media files are currently hosted on Uploadcare.
     This method checks if a media URL is hosted there.
     */
    isUCMedia() {
      try {
        const url = new URL(this.$attrs.src);
        return url.hostname === RAS_MEDIA_SERVER_HOSTNAME;
      } catch (err) {
        return false;
      }
    },
    /*
     We still have some internal media files hosted on legacy rocket
     servers:
      prod.media.rocketalumnisolutions.com
      dev.media.rocketalumnisolutions.com
      test.media.rocketalumnisolutions.com
     This method checks if a media URL is hosted there.
     */
    isRocketMedia() {
      try {
        const url = new URL(this.$attrs.src);
        return url.hostname.includes(".media.rocketalumnisolutions.com");
      } catch (err) {
        return false;
      }
    },
    isExternalMedia() {
      return !this.isRocketMedia && !this.isUCMedia;
    },
  },
  watch: {
    "$attrs.src": {
      handler() {
        this.init();
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.isExternalMedia && this.loadImageDimensions) {
      this.$el.onload = () => this.setDimensions(this.$el);
    }
  },
  methods: {
    getRockerUrl(url, width) {
      const decodedUrl = decodeURIComponent(url);
      const encodedMediaName = encodeURIComponent(decodedUrl.split("/").pop());
      const mediaBaseUrl = this.getMediaBaseURL(decodedUrl);
      const originalExt = encodedMediaName.split(".").pop().toLowerCase();
      const newExt = convertExt.get(originalExt) ?? originalExt;
      return `${mediaBaseUrl}/${encodedMediaName}/sizes/${this.getEncodedMediaNameNoExtension(
        decodedUrl
      )}`.concat(`_${width}.${newExt}`);
    },
    getUCUrl(url, width) {
      if (this.forceOriginalSize) {
        return url;
      }

      return `${url}-/resize/${width}x/`;
    },
    // generate the src url for the resized image
    getSrcUrl(url, width) {
      if (this.isRocketMedia) {
        return this.getRockerUrl(url, width);
      }
      if (this.isUCMedia) {
        return this.getUCUrl(url, width);
      }
      return null;
    },
    getEncodedMediaNameNoExtension(url) {
      const mediaName = url.split("/").pop();
      const split = mediaName.split(".");
      split.pop();
      return encodeURIComponent(split.join("."));
    },
    getMediaBaseURL(url) {
      const splitURL = url.split("/");
      // remove file name
      splitURL.pop();
      return splitURL.join("/");
    },
    createImg(attrs) {
      const img = document.createElement("img");
      Object.keys(attrs).forEach((key) => {
        img.setAttribute(key, attrs[key]);
      });
      img.onload = () => {
        img.remove();
      };
      img.onerror = () => {
        img.remove();
      };
      img.style.visibility = "hidden";
      img.style.height = 0;
      img.style.width = 0;
      document
        .getElementById(BODY_TARGET_IDS.imageLoaderTarget)
        .appendChild(img);
      return img;
    },
    async init() {
      // if external media then we don't make it responsive or progressive
      if (this.isExternalMedia) {
        // instantly emit loadComplete to avoid showing skeleton loader
        this.$emit("loadComplete");
        return;
      }

      const cacheHit = await doesMediaUrlExistInCache(this.$attrs.src);

      if (cacheHit) {
        this.cachedUrl = this.$attrs.src;
        return;
      }

      this.progressive = false;
      this.mainFailed = false;
      const mainImg = this.createImg(this.attrsMain);
      mainImg.onerror = () => {
        this.progressive = false;
        this.mainFailed = true;
      };
      if (this.loadImageDimensions) {
        mainImg.onload = () => {
          this.setDimensions(mainImg);
          this.$emit("loadComplete");
        };
      } else {
        mainImg.onload = () => {
          this.$emit("loadComplete");
        };
      }
      if (!mainImg.complete) {
        this.progressive = true;
        const progImg = this.createImg(this.attrsProg);
        progImg.onload = () => {
          this.progressive = false;
        };
      }
    },
    imageToBlob(imgElement) {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = imgElement.naturalWidth;
        canvas.height = imgElement.naturalHeight;
        context.drawImage(imgElement, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas toBlob conversion failed"));
          }
        });
      });
    },
    async setDimensions(img) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const dimensions = {
        height,
        width,
        aspectRatio: width / height,
        isLandscape: width > height,
        isPortrait: width < height,
        isSquare: width === height,
      };
      this.dimensions = {
        ...dimensions,
      };
    },
  },
};
</script>

<style scoped>
.responsive-img {
  transition: filter 0.3s;
}

.progressive-img {
  filter: blur(15px);
}

.main-img {
  filter: blur(0px);
}
</style>
